import { useEffect, type ReactNode } from "react";
import { homePathFor, useAuth } from "./auth/AuthContext";
import { EmptyState, Loading, PageHead } from "./components/ui";
import { AthleteLayout } from "./layouts/AthleteLayout";
import { ManagementLayout } from "./layouts/ManagementLayout";
import { OrganizationsPage } from "./pages/admin/OrganizationsPage";
import { AthleteActivitiesPage as MyActivitiesPage } from "./pages/athlete/AthleteActivities";
import { AthleteEvolutionPage, AthleteHomePage } from "./pages/athlete/AthleteDashboard";
import { GpsDiagnosticPage } from "./pages/athlete/GpsDiagnostic";
import { RunModePage } from "./pages/athlete/RunMode";
import { AthleteBodyPage, AthleteMessagesPage, AthleteProfilePage } from "./pages/athlete/AthletePages";
import { AthleteCalendarPage, WorkoutDetailPage } from "./pages/athlete/AthleteWorkouts";
import { ForcedPasswordChangePage, ForgotPasswordPage, LoginPage, ServerPage, SetPasswordPage, VerifyEmailPage } from "./pages/auth/AuthPages";
import { AthleteActivitiesPage } from "./pages/org/AthleteActivitiesPage";
import { AthleteDetailPage } from "./pages/org/AthleteDetailPage";
import { AthleteEvolutionPage as StaffEvolutionPage } from "./pages/org/AthleteEvolutionPage";
import { AthletesPage } from "./pages/org/AthletesPage";
import { CoachesPage } from "./pages/org/CoachesPage";
import { GroupsPage } from "./pages/org/GroupsPage";
import { OverviewPage } from "./pages/org/OverviewPage";
import { PlanPage } from "./pages/org/PlanPage";
import { TemplatesPage } from "./pages/org/TemplatesPage";
import { ExercisesPage } from "./pages/org/ExercisesPage";
import { AccountPage } from "./pages/shared/AccountPage";
import { ActivityDetailPage } from "./pages/shared/ActivityDetailPage";
import { AuditPage } from "./pages/shared/AuditPage";
import { HelpPage } from "./pages/shared/HelpPage";
import { AdminTicketPage, SupportInboxPage } from "./pages/admin/SupportInboxPage";
import { NewTicketPage, UserTicketPage } from "./pages/shared/TicketPages";
import { Link, matchPath, navigate, useLocation } from "./router/router";
import { t } from "./i18n";
import { getServerUrl, isApp } from "./lib/platform";

const PUBLIC: Record<string, () => ReactNode> = {
  "/entrar": () => <LoginPage />,
  "/servidor": () => <ServerPage />,
  "/esqueci-senha": () => <ForgotPasswordPage />,
  "/aceitar-convite": () => <SetPasswordPage mode="invite" />,
  "/redefinir-senha": () => <SetPasswordPage mode="reset" />,
  "/verificar-email": () => <VerifyEmailPage />,
};

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

function SelectOrg({ orgId, select }: { orgId: string; select: (id: string) => void }) {
  useEffect(() => select(orgId), [orgId, select]);
  return <Loading />;
}

function NotFound() {
  return (
    <>
      <PageHead title={t("Página não encontrada")} />
      <div className="panel">
        <EmptyState title={t("Este endereço não existe ou você não tem acesso a ele.")} action={<Link to="/">{t("Ir para o início")}</Link>} />
      </div>
    </>
  );
}

export function App() {
  const { status, me, membership, selectOrganization } = useAuth();
  const { pathname } = useLocation();

  // App de celular: sem servidor escolhido não há API para falar
  if (isApp && !getServerUrl()) return <ServerPage />;

  // Telas públicas: os links de convite/redefinição funcionam mesmo com outra sessão aberta.
  const publicPage = PUBLIC[pathname.replace(/\/+$/, "") || "/"];
  if (publicPage && (pathname !== "/entrar" || status === "anonymous")) return <>{publicPage()}</>;

  if (status === "loading") return <Loading label={t("Abrindo o EAPace…")} />;
  if (status === "anonymous" || !me) return <Redirect to="/entrar" />;
  if (me.must_change_password) return <ForcedPasswordChangePage />;
  if (pathname === "/" || pathname === "/entrar") return <Redirect to={homePathFor(me, membership)} />;

  if (pathname === "/conta") {
    const isAthleteOnly = membership && membership.roles.every((r) => r === "athlete") && !me.is_platform_admin;
    return isAthleteOnly ? (
      <AthleteLayout>
        <AccountPage />
      </AthleteLayout>
    ) : (
      <ManagementLayout>
        <AccountPage />
      </ManagementLayout>
    );
  }

  if (pathname.startsWith("/admin")) {
    if (!me.is_platform_admin) return <ManagementLayout><NotFound /></ManagementLayout>;
    const adminManual = matchPath("admin/ajuda/manual/:slug", pathname);
    const adminTicket = matchPath("admin/suporte/:ticketId", pathname);
    const page =
      pathname === "/admin/assessorias" ? <OrganizationsPage />
      : pathname === "/admin/auditoria" ? <AuditPage />
      : pathname === "/admin/suporte" ? <SupportInboxPage />
      : adminTicket ? <AdminTicketPage key={adminTicket.ticketId} ticketId={adminTicket.ticketId} />
      : pathname === "/admin/ajuda" ? <HelpPage area="admin" base="/admin/ajuda" />
      : adminManual ? <HelpPage key={adminManual.slug} area="admin" base="/admin/ajuda" slug={adminManual.slug} />
      : <NotFound />;
    return <ManagementLayout>{page}</ManagementLayout>;
  }

  if (pathname === "/sem-acesso") {
    return (
      <ManagementLayout>
        <PageHead title={t("Sem assessoria vinculada")} />
        <div className="panel">
          <EmptyState title={t("Sua conta ainda não está ligada a nenhuma assessoria.")}>
            {" "}{t("Peça ao gestor da sua assessoria para enviar um convite para {email}.", { email: me.email })}{" "}</EmptyState>
        </div>
      </ManagementLayout>
    );
  }

  const orgMatch = prefixMatch(pathname);
  if (orgMatch) {
    const { orgId, rest } = orgMatch;
    const target = me.organizations.find((o) => o.organization_id === orgId);
    if (!target) return <ManagementLayout><NotFound /></ManagementLayout>;
    if (membership?.organization_id !== orgId) return <SelectOrg orgId={orgId} select={selectOrganization} />;

    if (rest === "atleta" || rest.startsWith("atleta/")) {
      if (!target.roles.includes("athlete")) return <Redirect to={`/org/${orgId}`} />;
      const workout = matchPath("atleta/treinos/:workoutId", rest);
      const activity = matchPath("atleta/atividades/:activityId", rest);
      const helpManual = matchPath("atleta/ajuda/manual/:slug", rest);
      const page =
        rest === "atleta" ? <AthleteHomePage orgId={orgId} />
        : rest === "atleta/perfil" ? <AthleteProfilePage orgId={orgId} />
        : rest === "atleta/calendario" ? <AthleteCalendarPage orgId={orgId} />
        : rest === "atleta/evolucao" ? <AthleteEvolutionPage orgId={orgId} />
        : rest === "atleta/mensagens" ? <AthleteMessagesPage orgId={orgId} />
        : rest === "atleta/diagnostico-gps" ? <GpsDiagnosticPage />
        : rest === "atleta/correr" ? <RunModePage orgId={orgId} />
        : rest === "atleta/corpo" ? <AthleteBodyPage orgId={orgId} />
        : rest === "atleta/atividades" ? <MyActivitiesPage orgId={orgId} />
        : rest === "atleta/ajuda" ? <HelpPage area="athlete" orgId={orgId} base={`/org/${orgId}/atleta/ajuda`} />
        : helpManual ? <HelpPage key={helpManual.slug} area="athlete" orgId={orgId} base={`/org/${orgId}/atleta/ajuda`} slug={helpManual.slug} />
        : workout ? <WorkoutDetailPage key={workout.workoutId} orgId={orgId} workoutId={workout.workoutId} />
        : activity ? (
          <ActivityDetailPage
            key={activity.activityId}
            orgId={orgId}
            activityId={activity.activityId}
            back={{ to: `/org/${orgId}/atleta/atividades`, label: t("Atividades") }}
            workoutHref={(id) => `/org/${orgId}/atleta/treinos/${id}`}
            canEdit
            canRate
            self
          />
        )
        : <NotFound />;
      return <AthleteLayout>{page}</AthleteLayout>;
    }

    if (!target.roles.some((r) => r !== "athlete")) return <Redirect to={`/org/${orgId}/atleta`} />;
    let page: ReactNode = <NotFound />;
    const detail = matchPath("atletas/:athleteId", rest);
    const planMatch = matchPath("atletas/:athleteId/planilha", rest);
    const actsMatch = matchPath("atletas/:athleteId/atividades", rest);
    const actMatch = matchPath("atletas/:athleteId/atividades/:activityId", rest);
    const evoMatch = matchPath("atletas/:athleteId/evolucao", rest);
    const helpMatch = matchPath("ajuda/manual/:slug", rest);
    const ticketMatch = matchPath("ajuda/chamados/:ticketId", rest);
    if (rest === "") page = <OverviewPage orgId={orgId} />;
    else if (rest === "atletas") page = <AthletesPage orgId={orgId} />;
    else if (detail) page = <AthleteDetailPage key={detail.athleteId} orgId={orgId} athleteId={detail.athleteId} />;
    else if (planMatch) page = <PlanPage key={planMatch.athleteId} orgId={orgId} athleteId={planMatch.athleteId} />;
    else if (evoMatch) page = <StaffEvolutionPage key={evoMatch.athleteId} orgId={orgId} athleteId={evoMatch.athleteId} />;
    else if (actsMatch) page = <AthleteActivitiesPage key={actsMatch.athleteId} orgId={orgId} athleteId={actsMatch.athleteId} />;
    else if (actMatch)
      page = (
        <ActivityDetailPage
          key={actMatch.activityId}
          orgId={orgId}
          activityId={actMatch.activityId}
          back={{ to: `/org/${orgId}/atletas/${actMatch.athleteId}/atividades`, label: t("Atividades do atleta") }}
          workoutHref={() => `/org/${orgId}/atletas/${actMatch.athleteId}/planilha`}
          canEdit={target.permissions.includes("activities.manage")}
          canMessage={target.permissions.includes("messages.staff")}
        />
      );
    else if (rest === "modelos") page = <TemplatesPage orgId={orgId} />;
    else if (rest === "exercicios") page = <ExercisesPage orgId={orgId} />;
    else if (rest === "treinadores") page = <CoachesPage orgId={orgId} />;
    else if (rest === "grupos") page = <GroupsPage orgId={orgId} />;
    else if (rest === "auditoria") page = <AuditPage orgId={orgId} />;
    else if (rest === "ajuda") page = <HelpPage area="staff" orgId={orgId} base={`/org/${orgId}/ajuda`} />;
    else if (rest === "ajuda/chamados/novo") page = <NewTicketPage orgId={orgId} />;
    else if (ticketMatch) page = <UserTicketPage key={ticketMatch.ticketId} orgId={orgId} ticketId={ticketMatch.ticketId} />;
    else if (helpMatch) page = <HelpPage key={helpMatch.slug} area="staff" orgId={orgId} base={`/org/${orgId}/ajuda`} slug={helpMatch.slug} />;
    return <ManagementLayout>{page}</ManagementLayout>;
  }

  return (
    <ManagementLayout>
      <NotFound />
    </ManagementLayout>
  );
}

/** /org/:orgId[/resto...] -> { orgId, rest } */
function prefixMatch(pathname: string): { orgId: string; rest: string } | null {
  const m = /^\/org\/([^/]+)\/?(.*)$/.exec(pathname.replace(/\/+$/, ""));
  return m ? { orgId: decodeURIComponent(m[1]), rest: m[2] } : null;
}
